import { ChevronRight, Globe, Home, Phone, Wifi } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import mtn from "../../../assets/images/logoT.png";
import CGU from "../../../assets/model-fichier/cgu.pdf";
import { HOST } from "../../../Utils/Utils";

const accentMap = {
  yellow: {
    border: "border-yellow-400",
    shadow: "shadow-yellow-200",
    iconBg: "bg-yellow-400",
    iconColor: "text-gray-900",
    tagline: "text-yellow-500",
    bar: "bg-yellow-400",
    dot: "bg-yellow-400",
  },
  blue: {
    border: "border-gray-900",
    shadow: "shadow-gray-200",
    iconBg: "bg-gray-900",
    iconColor: "text-white",
    tagline: "text-gray-900",
    bar: "bg-gray-400",
    dot: "bg-gray-400",
  },
};

export const HomePage = () => {
  const [selected, setSelected] = useState(null);
  const { idAgent } = useParams();

  const OFFERS = [
    {
      id: "fibre",
      label: "Fibre",
      icon: Wifi,
      tagline: "Internet ultra-rapide",
      description:
        "Connexion fibre optique haut débit pour votre domicile ou entreprise.",
      badge: "Populaire",
      accent: "yellow",
      link: `${HOST}/${idAgent}`,
    },
    {
      id: "homebox",
      label: "Homebox",
      icon: Home,
      tagline: "Tout-en-un sans fil",
      description:
        "Box 4G/5G clé en main, sans installation, active en quelques minutes.",
      badge: null,
      accent: "blue",
      link: "https://mtnone.mtn.ci",
    },
  ];

  const selectedOffer = OFFERS.find((o) => o.id === selected);

  return (
    <div className="min-h-screen bg-[#f8f8f6] font-sans">
      {/* ── Header ── */}
      <header className="bg-[#FFCC00]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo + titre */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm shrink-0">
              <img src={mtn} alt="MTN" className="w-9" />
            </div>
            <div>
              <p className="font-mtn font-extrabold text-lg text-gray-900 leading-tight tracking-tight">
                MTN Côte d'Ivoire
              </p>
              <a
                href={CGU}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-gray-700 underline opacity-70 hover:opacity-100 transition-opacity"
              >
                Voir les Conditions Générales
              </a>
            </div>
          </div>

          {/* Contact — desktop */}
          <div className="hidden md:flex items-center gap-6">
            <div className="flex items-center gap-1.5 text-[13px] text-gray-900">
              <Phone size={14} />
              <span>100 (Service Client)</span>
            </div>
            <div className="flex items-center gap-1.5 text-[13px] text-gray-900">
              <Globe size={14} />
              <span>www.mtn.ci</span>
            </div>
          </div>
        </div>
      </header>

      {/* ── Hero / Selector ── */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-xs font-semibold tracking-[0.18em] text-yellow-500 uppercase mb-2">
          Choisissez votre offre
        </p>
        <h2 className="font-mtn text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
          Connectez-vous à votre façon
        </h2>
        <p className="text-[15px] text-gray-500 max-w-xl mb-10 leading-relaxed">
          Sélectionnez l'offre internet qui correspond à votre usage et profitez
          du meilleur réseau de Côte d'Ivoire.
        </p>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {OFFERS.map(
            ({
              id,
              label,
              icon: Icon,
              tagline,
              description,
              badge,
              accent,
              link,
            }) => {
              const isActive = selected === id;
              const a = accentMap[accent];

              return (
                <button
                  key={id}
                  onClick={() => {
                    window.location.href = link;
                  }}
                  className={[
                    "relative text-left bg-white rounded-2xl p-7 border-2 transition-all duration-200 cursor-pointer",
                    isActive
                      ? `${a.border} shadow-lg ${a.shadow}`
                      : "border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200",
                  ].join(" ")}
                >
                  {/* Badge */}
                  {badge && (
                    <span className="absolute top-5 right-5 bg-yellow-400 text-gray-900 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                      {badge}
                    </span>
                  )}

                  {/* Icon */}
                  <div
                    className={[
                      "w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-colors duration-200",
                      isActive ? a.iconBg : "bg-gray-100",
                    ].join(" ")}
                  >
                    <Icon
                      size={22}
                      strokeWidth={2}
                      className={isActive ? a.iconColor : "text-gray-400"}
                    />
                  </div>

                  <p className="font-extrabold text-xl text-gray-900 mb-1">
                    {label}
                  </p>
                  <p
                    className={`text-[13px] font-semibold mb-2.5 transition-colors duration-200 ${isActive ? a.tagline : "text-gray-300"}`}
                  >
                    {tagline}
                  </p>
                  <p className="text-[14px] text-gray-500 leading-relaxed">
                    {description}
                  </p>

                  {/* CTA row */}
                  <div
                    className={`flex items-center gap-1 mt-6 text-[13px] font-bold transition-colors duration-200 ${isActive ? "text-gray-900" : "text-gray-300"}`}
                  >
                    {isActive ? "Sélectionné" : "Choisir cette offre"}
                    <ChevronRight size={15} strokeWidth={2.5} />
                  </div>

                  {/* Bottom accent bar */}
                  <div
                    className={`absolute bottom-0 left-5 right-5 h-[3px] rounded-b transition-all duration-200 ${isActive ? a.bar : "bg-transparent"}`}
                  />
                </button>
              );
            },
          )}
        </div>

        {/* Confirmation pill */}
        {selected && (
          <div className="mt-8 inline-flex items-center gap-2.5 bg-white border border-gray-100 rounded-full px-5 py-3 text-sm font-semibold text-gray-600 shadow-sm animate-[fadeUp_0.25s_ease]">
            <span
              className={`w-2 h-2 rounded-full ${accentMap[selectedOffer.accent].dot}`}
            />
            Offre{" "}
            <strong className="text-gray-900">{selectedOffer.label}</strong>{" "}
            sélectionnée
            <button
              onClick={() => {
                selectedOffer.label === "Fibre"
                  ? (window.location.href = `${HOST}/${idAgent}`)
                  : (window.location.href = "https://mtnone.mtn.ci");
              }}
              className="ml-2 bg-gray-900 text-white text-xs font-bold px-4 py-1.5 rounded-full hover:bg-gray-700 transition-colors cursor-pointer"
            >
              Continuer →
            </button>
          </div>
        )}
      </main>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default HomePage;
